import _ from 'lodash';
import moment from 'moment-timezone';

import getDeliveryTime from 'local_modules/get_delivery_time';

export default ngInject(function(
  $state,
  $stateParams,
  $scope,
  $q,
  $sce,
  foodhub,
  Supplier,
  purchaseOrderFilterService,
  stateParamsHelper,
  ProcurementSchedule,
  toaster,
  confirmActionModal,
) {
  this.loading = true;
  this.loadingDeadlines = true;
  this.hasDeadlines = false;
  this.suppliers = [];
  this.purchaseOrders = purchaseOrderFilterService.query();
  this.posWithSuppliers = [];
  this.deadlineDay = $stateParams.deadlineDay || moment().format('YYYY-MM-DD');
  this.deadlineDays = _.range(-3, 7).map((offset) =>
    moment()
      .add(offset, 'days')
      .format('YYYY-MM-DD'),
  );
  this.ownerNames = [];
  this.ownerNameFilter = null;
  this.capacityFilter = false;
  this.deadlines = [];
  this.filteredDeadlines = [];

  this.changeDeadlineDay = (deadlineDay) => {
    if ($stateParams.deadlineDay !== deadlineDay) {
      stateParamsHelper.setStateParam('deadlineDay', deadlineDay);
    }

    this.loadingDeadlines = true;
    this.hasDeadlines = false;
    return ProcurementSchedule.upcomingDeadlines({foodhubSlug: foodhub.slug, deadlineDay})
      .then((deadlines) => {
        this.deadlines = _(deadlines)
          .map((deadline) =>
            Object.assign({}, deadline, {
              supplierName:
                this.suppliersById[deadline.vendorId] != null
                  ? this.suppliersById[deadline.vendorId].name
                  : undefined,
              supplierSlug:
                this.suppliersById[deadline.vendorId] != null
                  ? this.suppliersById[deadline.vendorId].slug
                  : undefined,
            }),
          )
          .sortBy(
            (deadline) =>
              // Unmet deadlines first, then sorted by time they are due
              `${(deadline.isMetByPurchaseOrder && 'z') || 'a'} ${moment
                .tz(deadline.dueAt, deadline.tzid)
                .format('YYYY-MM-DD HH:mm')}`,
          )
          .value();

        this.ownerNames = _(deadlines)
          .map((deadline) => deadline.ownerName)
          .uniq()
          .compact()
          .sort()
          .value();
        this.filterDeadlines();
        this.hasDeadlines = deadlines.length > 0;
        this.loadingDeadlines = false;
      })
      .catch((err) => {
        toaster.serverFailCB()(err);
        this.loadingDeadlines = false;
      });
  };

  $q.all([
    Supplier.getSuppliers().then((suppliers) => {
      this.suppliers = suppliers;
      this.suppliersById = _.keyBy(this.suppliers, ({id}) => id);
    }),
    this.purchaseOrders.$promise,
  ])
    .then(() => {
      const poSupplierIds = _(this.purchaseOrders)
        .map(({vendorId}) => vendorId)
        .uniq()
        .value();
      this.posWithSuppliers = this.purchaseOrders.map((po) =>
        Object.assign({}, po, {
          supplierName:
            this.suppliersById[po.vendorId] != null
              ? this.suppliersById[po.vendorId].name
              : undefined,
          supplierSlug:
            this.suppliersById[po.vendorId] != null
              ? this.suppliersById[po.vendorId].slug
              : undefined,
        }),
      );

      if (this.showingSearch) {
        // only show vendors who correspond to current search
        this.suppliers = this.suppliers.filter(
          (supplier) =>
            $stateParams.searchBy == null ||
            !isNaN($stateParams.searchBy) ||
            (supplier.name != null ? supplier.name.indexOf($stateParams.searchBy) : undefined) >=
              0 ||
            supplier.slug.includes($stateParams.searchBy) ||
            poSupplierIds.includes(supplier.id),
        );
      }

      if (this.showingUpcomingPOs) {
        return this.changeDeadlineDay(this.deadlineDay);
      }
    })
    .finally(() => (this.loading = false));

  this.selectTab = (tab) => {
    if (tab == null) {
      tab = 'upcoming';
    }
    this.showingSearch = tab === 'search';
    this.showingUpcomingPOs = tab === 'upcoming';
    if ($stateParams.activeTab !== tab) {
      stateParamsHelper.setStateParam('activeTab', tab);
    }
  };

  this.selectTab($stateParams.activeTab);

  this.getDeliveryTime = (purchaseOrder) => getDeliveryTime.start(purchaseOrder, foodhub.tzid);

  this.formatTimeOfDay = function(time, includeAMorPM, param) {
    if (includeAMorPM == null) {
      includeAMorPM = true;
    }
    if (param == null) {
      param = {};
    }
    const {tzid} = param;
    let formatter = 'h:mm';
    if (includeAMorPM) {
      formatter += 'a';
    }
    const tzTime = tzid != null ? moment.tz(time, tzid) : moment(time, 'HH:mm');
    return tzTime.format(formatter).replace(':00', '');
  };

  this.formatDay = function(day) {
    if (day === moment().format('YYYY-MM-DD')) {
      return 'Today';
    }
    const date = moment(day, 'YYYY-MM-DD');
    return $sce.trustAsHtml(
      `<span class='day'>${date.format('dddd')}</span> <span class='date'>${date.format(
        ' M/D',
      )}</span>`,
    );
  };

  this.formatDeliveryWindow = ({dropoffWindow, dropoffDay}) => {
    const str = `${moment(dropoffDay).format('ddd M/D/YY')} ${this.formatTimeOfDay(
      dropoffWindow.startAt,
    )}-${this.formatTimeOfDay(dropoffWindow.endAt)}`;
    return str.replace(/ /g, '\u00A0\u00A0'); // double space
  };

  this.formatSubmitter = function(deadline) {
    if (deadline.ownerName != null) {
      return deadline.ownerName;
    }
    if (!deadline.lastSubmittedBy) {
      return;
    }
    return deadline.lastSubmittedBy.split('@')[0].replace(/\./g, ' ');
  };

  this.isPastDue = function(deadline) {
    if (deadline.isMetByPurchaseOrder) {
      return false;
    }
    return moment(deadline.dueAt).isBefore(moment().subtract(this.upcomingDaysOut, 'days'));
  };

  this.skipDeadline = function(deadline) {
    return confirmActionModal
      .open('skip this purchase order')
      .then(() =>
        ProcurementSchedule.skip({
          id: deadline.procurementScheduleId,
          dropoffDay: deadline.dropoffDay,
        }),
      )
      .then(() => {
        // eslint-disable-next-line goodeggs/lodash-prefer-immutable-method
        _.pull(this.deadlines, deadline);
        // eslint-disable-next-line goodeggs/lodash-prefer-immutable-method
        _.pull(this.filteredDeadlines, deadline);
      })
      .catch(function(err) {
        if (err == null) {
          return;
        } // Cancel from confirm modal
        return toaster.serverFailCB()(err);
      });
  };

  this.filterDeadlines = function() {
    return (this.filteredDeadlines = this.deadlines.filter((deadline) => {
      if (deadline.supplierName == null) {
        return false;
      }
      if (this.ownerNameFilter && deadline.ownerName !== this.ownerNameFilter) {
        return false;
      }
      if (this.capacityFilter && !deadline.isCapacityEnabled) {
        return false;
      }
      return true;
    }));
  };

  this.filterByOwnerName = function(ownerName) {
    this.ownerNameFilter = ownerName;
    return this.filterDeadlines();
  };

  this.filterByCapacity = function() {
    return this.filterDeadlines();
  };

  return this;
}); // http://stackoverflow.com/questions/28953289/using-controller-as-with-the-ui-router-isnt-working-as-expected
